import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatINR } from "../lib/api";
import { Check, X, AlertCircle, ShieldCheck } from "lucide-react";

export default function PaymentTest() {
  const [params] = useSearchParams();
  const txId = params.get("tx_id");
  const navigate = useNavigate();
  const [tx, setTx] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    api.get(`/payments/status/${txId}`).then((r) => setTx(r.data)).catch(() => setTx({ missing: true }));
  }, [txId]);

  const result = async (outcome) => {
    setBusy(outcome);
    try {
      const r = await api.post(`/payments/test/${txId}/result`, { outcome });
      if (r.data.payment_status === "paid") navigate(`/payment/success?tx_id=${txId}`);
      else setTx((t) => ({ ...t, payment_status: r.data.payment_status }));
    } catch { setBusy(""); }
  };

  return (
    <div className="min-h-screen bg-cream dot-grid flex items-center justify-center p-5">
      <div className="w-full max-w-md bg-white rounded-3xl card-outline sticker p-7" data-testid="test-payment-page">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-coral"><ShieldCheck className="w-4 h-4" /> Test payment page</p>
        <h1 className="font-display font-semibold text-2xl mt-2">Haven Hives — Sandbox</h1>
        <p className="text-sm text-charcoal/60 mt-1">This is a clearly-labelled demonstration gateway. No real money moves and no card details are collected.</p>
        {tx && !tx.missing && (
          <dl className="mt-5 bg-cream rounded-2xl p-4 text-sm space-y-1.5">
            <div className="flex justify-between"><dt>Order</dt><dd className="font-semibold">{tx.order_number}</dd></div>
            <div className="flex justify-between"><dt>Amount</dt><dd className="font-bold">{formatINR(tx.amount || 0)}</dd></div>
          </dl>
        )}
        {tx?.missing && <p role="alert" className="text-sm text-coral mt-4">This payment session has expired. Please start checkout again.</p>}
        {tx?.payment_status === "failed" && (
          <div className="mt-4 bg-coral/10 rounded-2xl p-4 text-sm" role="alert" data-testid="payment-failed-state">
            <p className="font-semibold text-coral flex items-center gap-1.5"><AlertCircle className="w-4 h-4" /> Payment failed</p>
            <p className="text-charcoal/70 mt-1">Your order wasn't placed and you haven't been charged. You can retry payment from the checkout.</p>
            <Link to="/checkout" className="mt-3 inline-block bg-coral text-white font-semibold px-5 py-2.5 rounded-full text-xs">Retry payment</Link>
          </div>
        )}
        {tx?.payment_status === "cancelled" && (
          <div className="mt-4 bg-blush/40 rounded-2xl p-4 text-sm" role="status" data-testid="payment-cancelled-state">
            <p className="font-semibold text-coral flex items-center gap-1.5"><X className="w-4 h-4" /> Payment cancelled</p>
            <p className="text-charcoal/70 mt-1">No payment was taken and your order wasn't placed.</p>
            <Link to="/checkout" className="mt-3 inline-block bg-coral text-white font-semibold px-5 py-2.5 rounded-full text-xs">Back to checkout</Link>
          </div>
        )}
        <div className="mt-6 space-y-3">
          <button onClick={() => result("success")} disabled={busy || tx?.missing} data-testid="test-pay-success"
            className="w-full bg-forest text-white font-semibold py-3.5 rounded-full sticker disabled:opacity-50 flex items-center justify-center gap-2">
            <Check className="w-4 h-4" /> {busy === "success" ? "Processing…" : "Simulate successful payment"}
          </button>
          <button onClick={() => result("failure")} disabled={busy || tx?.missing} data-testid="test-pay-failure"
            className="w-full bg-butter text-charcoal font-semibold py-3.5 rounded-full sticker disabled:opacity-50 flex items-center justify-center gap-2">
            <AlertCircle className="w-4 h-4" /> Simulate failed payment
          </button>
          <button onClick={() => result("cancel")} disabled={busy || tx?.missing} data-testid="test-pay-cancel"
            className="w-full border border-charcoal/20 font-semibold py-3.5 rounded-full disabled:opacity-50 flex items-center justify-center gap-2">
            <X className="w-4 h-4" /> Cancel payment
          </button>
        </div>
        <p className="text-[11px] text-charcoal/50 mt-5">When your Razorpay keys are added in Admin → Settings, this page is replaced by the real hosted gateway (UPI, cards, net banking).</p>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-cream dot-grid flex items-center justify-center p-5">
      <div className="w-full max-w-md bg-white rounded-3xl card-outline sticker p-7 text-center" data-testid="payment-cancelled-page">
        <div className="w-14 h-14 rounded-full bg-blush flex items-center justify-center mx-auto"><X className="w-7 h-7 text-coral" /></div>
        <h1 className="font-display font-semibold text-2xl mt-4">Payment cancelled</h1>
        <p className="text-sm text-charcoal/70 mt-2">No payment was made and your order wasn't placed. Your bag is safe if you'd like to try again.</p>
        <div className="flex flex-col sm:flex-row gap-3 mt-6 justify-center">
          <Link to="/checkout" data-testid="payment-cancel-retry" className="bg-coral text-white font-semibold px-6 py-3 rounded-full sticker">Back to checkout</Link>
          <Link to="/cart" className="border border-charcoal/20 font-semibold px-6 py-3 rounded-full">View bag</Link>
        </div>
      </div>
    </div>
  );
}
